import { Link } from "react-router-dom";
import api from "../lib/api.js";
import { useAsync } from "../lib/hooks.js";
import { useAuth } from "../lib/auth.jsx";
import {
  Card,
  ErrorState,
  Loading,
  PageIntro,
  SeverityBadge,
  Stat,
  StateBadge,
  Empty,
  formatNumber,
  formatRelative,
} from "../components/ui.jsx";
import {
  IconAlert,
  IconBolt,
  IconDevices,
  IconGateway,
  IconPlus,
  IconChevronRight,
} from "../components/icons.jsx";

export default function Dashboard() {
  const { isAdmin } = useAuth();

  const devices = useAsync(() => api.listDevices({ limit: 500 }), []);
  const alerts = useAsync(() => api.alerts({ limit: 8 }), []);
  const usage = useAsync(
    () =>
      api.tenantUsage({
        from: new Date(Date.now() - 7 * 86400_000).toISOString(),
        to: new Date().toISOString(),
        granularity: "day",
      }),
    []
  );

  if (devices.loading) return <Loading label="Loading your estate" />;
  if (devices.error) return <ErrorState error={devices.error} onRetry={devices.reload} />;

  const list = devices.data ?? [];
  const active = list.filter((d) => d.State === "ACTIVE").length;
  const gateways = list.filter((d) => d.Kind === "NOVALINK").length;
  const offline = list.filter((d) => d.State === "ACTIVE" && isStale(d.LastSeenAt)).length;

  const weekEnergy = (usage.data?.buckets ?? []).reduce((sum, b) => sum + (b.active_energy ?? 0), 0);
  const critical = (alerts.data ?? []).filter((a) => a.Severity === "CRITICAL").length;

  return (
    <>
      <PageIntro title="Estate overview">
        Everything issued to your tenant, at a glance. Start from here to find a device,
        send a command or check consumption. Figures cover the last seven days.
      </PageIntro>

      <div className="grid grid-4" style={{ marginBottom: 20 }}>
        <Stat
          label="Devices"
          value={formatNumber(list.length, 0)}
          sub={`${active} in service`}
          icon={IconDevices}
        />
        <Stat
          label="Gateways"
          value={formatNumber(gateways, 0)}
          sub="NovaLink concentrators"
          icon={IconGateway}
        />
        <Stat
          label="Energy this week"
          value={`${formatNumber(weekEnergy, 1)} kWh`}
          sub="Sum of device registers"
          icon={IconBolt}
        />
        <Stat
          label="Not reporting"
          value={formatNumber(offline, 0)}
          sub={offline ? "Active but silent for over 24 h" : "All active devices reporting"}
          icon={IconAlert}
        />
      </div>

      {critical > 0 && (
        <div className="callout callout-danger" style={{ marginBottom: 20 }}>
          <IconAlert />
          <div>
            <strong>{critical} critical alert{critical === 1 ? "" : "s"} need attention.</strong>{" "}
            <Link to="/alerts">Review them now</Link>.
          </div>
        </div>
      )}

      <div className="grid grid-2">
        <Card
          title="Recently seen"
          hint="The devices that reported most recently"
          actions={
            <Link className="btn btn-secondary btn-sm" to="/devices">
              All devices <IconChevronRight />
            </Link>
          }
        >
          {list.length === 0 ? (
            <Empty title="No devices yet">
              {isAdmin
                ? "Register hardware models first, then create a production run and issue the units to a tenant."
                : "Once devices are issued to your tenant they will appear here."}
              {isAdmin && (
                <div style={{ marginTop: 14 }}>
                  <Link className="btn btn-primary" to="/models">
                    <IconPlus /> Add a hardware model
                  </Link>
                </div>
              )}
            </Empty>
          ) : (
            <div className="table-wrap" style={{ border: "none" }}>
              <table>
                <thead>
                  <tr>
                    <th>Device</th>
                    <th>Kind</th>
                    <th>State</th>
                    <th>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {[...list]
                    .sort((a, b) => new Date(b.LastSeenAt ?? 0) - new Date(a.LastSeenAt ?? 0))
                    .slice(0, 8)
                    .map((d) => (
                      <tr key={d.Number}>
                        <td>
                          <Link className="mono" to={`/devices/${d.Number}`}>
                            {d.Number}
                          </Link>
                        </td>
                        <td>{kindLabel(d.Kind)}</td>
                        <td>
                          <StateBadge state={d.State} />
                        </td>
                        <td className="hint">{formatRelative(d.LastSeenAt)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card
          title="Latest alerts"
          hint="Events reported by devices in the field"
          actions={
            <Link className="btn btn-secondary btn-sm" to="/alerts">
              All alerts <IconChevronRight />
            </Link>
          }
        >
          {alerts.loading ? (
            <Loading />
          ) : (alerts.data ?? []).length === 0 ? (
            <Empty title="Nothing reported">
              Devices have not raised any events. Alerts appear here as soon as one does.
            </Empty>
          ) : (
            <div className="table-wrap" style={{ border: "none" }}>
              <table>
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Device</th>
                    <th>Severity</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.data.map((a) => (
                    <tr key={a.ID}>
                      <td>{a.EventCode}</td>
                      <td>
                        <Link className="mono" to={`/devices/${a.DeviceNumber}`}>
                          {a.DeviceNumber}
                        </Link>
                      </td>
                      <td>
                        <SeverityBadge severity={a.Severity} />
                      </td>
                      <td className="hint">{formatRelative(a.OccurredAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

/** A device that has not reported in a day is worth surfacing. */
function isStale(lastSeen) {
  if (!lastSeen) return true;
  return Date.now() - new Date(lastSeen).getTime() > 86400_000;
}

export function kindLabel(kind) {
  return (
    {
      BRANCH: "Branch",
      BRANCH_PLUS: "Branch+",
      NODEFLEX: "NodeFlex",
      NODENET: "NodeNet",
      POWERSHARE: "PowerShare",
      NOVALINK: "NovaLink",
    }[kind] ?? kind
  );
}
